import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  IconButton,
  Chip,
  List,
  ListItemButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Alert,
  CircularProgress,
  Divider,
  Autocomplete,
  useTheme,
  useMediaQuery,
  Drawer,
  Collapse,
  SelectChangeEvent,
  Grid,
  LinearProgress,
  Fade,
  Snackbar,
  Breadcrumbs,
  Link,
  Avatar,
  Stack,
  FormControlLabel,
  Switch,
  InputAdornment,
} from "@mui/material";
import {
  Add,
  Save,
  ArrowBack,
  VideoLibrary,
  PictureAsPdf,
  TextFields,
  ExpandMore,
  ExpandLess,
  Close,
  CloudUpload,
  School,
  Menu as MenuIcon,
  ContentCopy,
  FolderOpen,
  PlayCircle,
  Description,
  Article,
  ArrowUpward,
  ArrowDownward,
  CheckCircle,
  Warning,
  AutoAwesome,
  Timeline,
  NavigateNext,
  Quiz,
  Delete,
  RadioButtonChecked,
  CheckBox as CheckBoxIcon,
  Notes,
  ArrowDropDown,

} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import {
  coursesAPI,
  videosAPI,

  usersAdminAPI,
  CoursePhasePayload,
} from "../../services/adminApi";
import { PDFViewer } from "../../components/admin/PDFViewer";
import { VideoPlayer } from "../../components/common/VideoPlayer";
import { resolveVideoPlayback } from "../../utils/videoPlayback";
import RichTextEditor from "../../components/UI/RichTextEditor";

// --- Survey Types ---
interface SurveyQuestion {
  id: string;
  text: string;
  type: "radio" | "checkbox" | "text" | "dropdown";
  options: string[];
  correctAnswer?: string | string[];
  required: boolean;
}

interface SurveyConfig {
  mode: "survey" | "quiz";
  description?: string;
  descriptionAr?: string;
  passingScore?: number;
  questions: SurveyQuestion[];
}

// --- Types ---
interface VideoOption {
  _id: string;
  title: string;
  duration: number;
  status: string;
  thumbnailUrl: string;
  playbackUrl?: string;
  cloudflareVideoId?: string;
  iframeUrl?: string;
}

interface PhaseItem {
  id: string;
  type: "video" | "pdf" | "letter" | "survey";
  title: string;
  titleAr?: string;
  video?: string;
  videoData?: VideoOption;
  pdfUrl?: string;
  pdfUrlAr?: string;
  pdfPages?: number;
  /** PDF items only — the video sold alongside this leaflet. */
  companionVideo?: string;
  companionVideoData?: VideoOption;
  letterContent?: string;
  letterContentAr?: string;
  surveyConfig?: SurveyConfig;
  order: number;
}

interface Phase {
  id: string;
  title: string;
  titleAr?: string;
  order: number;
  items: PhaseItem[];
  expanded: boolean;
}

// --- Constants ---
const PHASE_SIDEBAR_WIDTH = 320;

const COLORS = {
  accent:       "#1565C0",
  accentDark:   "#0D47A1",
  accentLight:  "#EEF4FF",
  accentMedium: "#DDEAFF",
  bg:           "#F0F7FF",
  text:         "#0A1628",
  textMuted:    "#78909C",
  textLight:    "#90A4AE",
  sidebar:      "#FFFFFF",
  border:       "#E8EDF5",
  borderHover:  "#1565C0",
  video:        "#1565C0",
  videoBg:      "rgba(21,101,192,0.08)",
  pdf:          "#EF4444",
  pdfBg:        "#FEF2F2",
  letter:       "#00897B",
  letterBg:     "#E0F2F1",
  success:      "#10B981",
  warning:      "#F59E0B",
  error:        "#EF4444",
  white:        "#FFFFFF",
  cardBg:       "#FFFFFF",
};

const COLORS_SURVEY = "#7C3AED";
const COLORS_SURVEY_BG = "#F5F3FF";

const ITEM_CONFIG: Record<string, { color: string; bg: string; icon: React.ComponentType<any>; label: string }> = {
  video: { color: COLORS.video, bg: COLORS.videoBg, icon: VideoLibrary, label: "Video" },
  pdf: { color: COLORS.pdf, bg: COLORS.pdfBg, icon: PictureAsPdf, label: "PDF" },
  letter: { color: COLORS.letter, bg: COLORS.letterBg, icon: TextFields, label: "Letter" },
  survey: { color: COLORS_SURVEY, bg: COLORS_SURVEY_BG, icon: Quiz, label: "Survey / Quiz" },
};

let _idCounter = 0;
const genId = () => `_${++_idCounter}_${Date.now()}`;

// Arabic inputs render RTL in an Arabic-capable face so editors see exactly
// what members will see on the course pages.
const ARABIC_INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    direction: "rtl" as const,
    fontFamily: "'Cairo', 'Segoe UI', sans-serif",
  },
  "& .MuiFormHelperText-root": { textAlign: "start" as const },
};

/**
 * One translatable value, edited in both languages side by side.
 *
 * English is the canonical value the API always stores; Arabic is optional
 * and falls back to English on the member pages when left empty.
 */
interface BilingualFieldProps {
  label: string;
  valueEn: string;
  valueAr: string;
  onChangeEn: (v: string) => void;
  onChangeAr: (v: string) => void;
  placeholderEn?: string;
  placeholderAr?: string;
  helperText?: string;
  multiline?: boolean;
  rows?: number;
  startAdornment?: React.ReactNode;
  sx?: any;
}

const BilingualField: React.FC<BilingualFieldProps> = ({
  label, valueEn, valueAr, onChangeEn, onChangeAr,
  placeholderEn, placeholderAr, helperText, multiline, rows, startAdornment, sx,
}) => (
  <Grid container spacing={2} sx={sx}>
    <Grid size={{ xs: 12, md: 6 }}>
      <TextField
        fullWidth
        label={`${label} (English)`}
        value={valueEn}
        onChange={(e) => onChangeEn(e.target.value)}
        placeholder={placeholderEn}
        multiline={multiline}
        rows={multiline ? rows ?? 3 : undefined}
        helperText={helperText}
        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
        InputProps={startAdornment ? { startAdornment } : undefined}
      />
    </Grid>
    <Grid size={{ xs: 12, md: 6 }}>
      <TextField
        fullWidth
        label={`${label} (العربية)`}
        value={valueAr}
        onChange={(e) => onChangeAr(e.target.value)}
        placeholder={placeholderAr}
        multiline={multiline}
        rows={multiline ? rows ?? 3 : undefined}
        helperText="Optional — falls back to English when empty"
        sx={ARABIC_INPUT_SX}
      />
    </Grid>
  </Grid>
);

const formatDuration = (seconds: number) => {
  if (!seconds || seconds < 0) return "\u2014";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const truncate = (str: string, max: number) =>
  str.length > max ? str.slice(0, max) + "\u2026" : str;

const getPlainTextFromHtml = (html?: string): string => {
  if (!html) return "";
  return html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "")
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

// ===== PDF PREVIEW - streams through backend to avoid CORS/R2 access issues =====
const PdfPreview: React.FC<{ pdfKey: string; title: string }> = ({ pdfKey, title }) => {
  const [blobUrl, setBlobUrl] = React.useState<string>("");
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!pdfKey) { setLoading(false); return; }
    let objectUrl = "";
    setLoading(true);
    setBlobUrl("");

    coursesAPI.streamPdf(pdfKey)
      .then(res => {
        const blob = new Blob([res.data], { type: "application/pdf" });
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      })
      .catch(() => setBlobUrl(""))
      .finally(() => setLoading(false));

    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [pdfKey]);

  if (loading) return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 400 }}>
      <Typography sx={{ color: "#6B7280", fontSize: ".9rem" }}>Loading PDF...</Typography>
    </Box>
  );

  if (!blobUrl) return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 400 }}>
      <Typography sx={{ color: "#EF4444", fontSize: ".9rem" }}>Failed to load PDF preview.</Typography>
    </Box>
  );

  return <PDFViewer url={blobUrl} title={title} />;
};

// ===== COMPONENT =====
const CourseEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEditing = id && id !== "new";
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Course State
  const [title, setTitle] = useState("");
  const [titleAr, setTitleAr] = useState("");
  const [description, setDescription] = useState("");
  const [descriptionAr, setDescriptionAr] = useState("");
  const [thumbnail, setThumbnail] = useState("");
  const [status, setStatus] = useState<string>("draft");
  const [sessionType, setSessionType] = useState<string>("");
  const [assignedUserId, setAssignedUserId] = useState<string>("");
  const [phases, setPhases] = useState<Phase[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  // UI State
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" as "success" | "error" });
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);

  // Active Selection
  const [activePhaseId, setActivePhaseId] = useState<string | null>(null);
  const [activeItemId, setActiveItemId] = useState<string | null>(null);

  // Add Item Dialog
  const [addItemDialogOpen, setAddItemDialogOpen] = useState(false);
  const [addItemPhaseId, setAddItemPhaseId] = useState<string>("");
  const [addItemType, setAddItemType] = useState<"video" | "pdf" | "letter" | "survey">("video");

  // Delete Confirm
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: "phase" | "item"; phaseId: string; itemId?: string } | null>(null);

  // Videos library
  const [availableVideos, setAvailableVideos] = useState<VideoOption[]>([]);
  // Companion-video upload — only one item is expanded at a time, so a single
  // ref/progress trio is enough (same pattern as the PDF inputs).
  const companionInputRef = useRef<HTMLInputElement>(null);
  const [companionUploading, setCompanionUploading] = useState(false);
  const [companionProgress, setCompanionProgress] = useState(0);
  const [companionError, setCompanionError] = useState("");
  const [videosLoading, setVideosLoading] = useState(false);
  const [videosLoadError, setVideosLoadError] = useState("");
  const [videoPreview, setVideoPreview] = useState<VideoOption | null>(null);
  const [brokenThumbs, setBrokenThumbs] = useState<Record<string, boolean>>({});
  // Stream asset or plain media file — the preview player needs to know which.
  const videoPreviewPlayback = useMemo(() => resolveVideoPlayback(videoPreview), [videoPreview]);


  // User options for private session assignment
  const [userOptions, setUserOptions] = useState<{ _id: string; name: string; email: string }[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // PDF upload
  const [pdfUploading, setPdfUploading] = useState(false);
  const [pdfUploadingAr, setPdfUploadingAr] = useState(false);
  const [thumbUploading, setThumbUploading] = useState(false);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const pdfInputArRef = useRef<HTMLInputElement>(null);
  const thumbInputRef = useRef<HTMLInputElement>(null);
  const [pdfEditorLang, setPdfEditorLang] = useState<"en" | "ar">("en");
  const [letterEditorLang, setLetterEditorLang] = useState<"en" | "ar">("en");

  // Computed Stats
  const stats = useMemo(() => {
    const totalItems = phases.reduce((s, p) => s + p.items.length, 0);
    // Counted the way the member-facing catalog counts it: video lessons plus
    // the companion video a leaflet carries.
    const videoCount = phases.reduce(
      (s, p) => s + p.items.filter((i) => i.type === "video" || i.companionVideo).length,
      0
    );
    const pdfCount = phases.reduce((s, p) => s + p.items.filter((i) => i.type === "pdf").length, 0);
    const letterCount = phases.reduce((s, p) => s + p.items.filter((i) => i.type === "letter").length, 0);
    const surveyCount = phases.reduce((s, p) => s + p.items.filter((i) => i.type === "survey").length, 0);
    return { totalItems, videoCount, pdfCount, letterCount, surveyCount, phaseCount: phases.length };
  }, [phases]);

  const markChanged = () => setHasChanges(true);

  // Load Course
  useEffect(() => {
    if (isEditing) loadCourse();
    loadVideos();
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadCourse = async () => {
    try {
      setLoading(true);
      const res = await coursesAPI.get(id!);
      const c = res.data;
      setTitle(c.title);
      setTitleAr(c.titleAr || "");
      setDescription(c.description || "");
      setDescriptionAr(c.descriptionAr || "");
      setThumbnail(c.thumbnail || "");
      setStatus(c.status || "draft");
      setSessionType(c.sessionType || "");
      setAssignedUserId(c.assignedUser?._id || "");

      const loaded: Phase[] = (c.phases || []).map((p: any, idx: number) => ({
        id: genId(),
        title: p.title,
        titleAr: p.titleAr || "",
        order: p.order ?? idx,
        expanded: idx === 0,
        items: (p.items || []).map((item: any, iIdx: number) => ({
          id: genId(),
          type: item.type,
          title: item.title,
          titleAr: item.titleAr || "",
          video: item.video?._id || item.video || "",
          videoData: item.video?._id
            ? {
                _id: item.video._id,
                title: item.video.title,
                duration: item.video.duration,
                status: item.video.status,
                thumbnailUrl: item.video.thumbnailUrl,
                playbackUrl: item.video.playbackUrl,
                cloudflareVideoId: item.video.cloudflareVideoId,
                iframeUrl: item.video.iframeUrl || undefined,
              }
            : undefined,
          pdfUrl: item.pdfUrl || "",
          pdfUrlAr: item.pdfUrlAr || "",
          companionVideo: item.companionVideo?._id || item.companionVideo || "",
          companionVideoData: item.companionVideo?._id
            ? {
                _id: item.companionVideo._id,
                title: item.companionVideo.title,
                duration: item.companionVideo.duration,
                status: item.companionVideo.status,
                thumbnailUrl: item.companionVideo.thumbnailUrl,
                playbackUrl: item.companionVideo.playbackUrl,
                cloudflareVideoId: item.companionVideo.cloudflareVideoId,
                iframeUrl: item.companionVideo.iframeUrl || undefined,
              }
            : undefined,
          pdfPages: item.pdfPages || 0,
          letterContent: item.letterContent || "",
          letterContentAr: item.letterContentAr || "",
          surveyConfig: item.surveyConfig
            ? {
                mode: item.surveyConfig.mode || "survey",
                description: item.surveyConfig.description || "",
                descriptionAr: item.surveyConfig.descriptionAr || "",
                passingScore: item.surveyConfig.passingScore,
                questions: (item.surveyConfig.questions || []).map((q: any) => ({
                  id: q.id || genId(),
                  text: q.text || "",
                  type: q.type || "radio",
                  options: q.options || [],
                  correctAnswer: q.correctAnswer,
                  required: q.required ?? true,
                })),
              }
            : undefined,
          order: item.order ?? iIdx,
        })),
      }));
      setPhases(loaded);
      if (loaded.length > 0) setActivePhaseId(loaded[0].id);
      setHasChanges(false);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load course");
    } finally {
      setLoading(false);
    }
  };

  const loadVideos = async () => {
    try {
      setVideosLoading(true);
      setVideosLoadError("");

      // Pull from the same admin gallery source, across pages, with no status filter.
      const allVideos: VideoOption[] = [];
      let page = 1;
      let pages = 1;

      do {
        const res = await videosAPI.list({ page, limit: 100 });
        const videos = res.data?.videos || [];
        const pagination = res.data?.pagination;

        allVideos.push(...videos);
        pages = pagination?.pages || 1;
        page += 1;
      } while (page <= pages);

      // The gallery returns raw video documents; playback is resolved where the
      // video is actually rendered, so nothing is derived here.
      const mapped: VideoOption[] = allVideos.map((video: any) => ({ ...video }));
      setAvailableVideos(mapped);
      return mapped;
    } catch (err: any) {
      setAvailableVideos([]);
      setVideosLoadError(err?.response?.data?.message || "Failed to load videos from gallery");
      return [];
    } finally {
      setVideosLoading(false);
    }
  };

  /**
   * Upload a companion video straight from the leaflet editor.
   *
   * Goes through the same Cloudflare direct-upload path as the video gallery,
   * then links the result to this PDF item — so an admin never has to leave the
   * course to attach one.
   */
  const handleCompanionUpload = async (file: File, phaseId: string, itemId: string) => {
    setCompanionError("");
    setCompanionUploading(true);
    setCompanionProgress(0);
    try {
      const title = file.name.replace(/\.[^.]+$/, "");
      const { videoId } = await videosAPI.uploadFile(file, title, setCompanionProgress);
      await videosAPI.confirmUpload(videoId).catch(() => {
        // Cloudflare finishes encoding asynchronously; a confirm that is not
        // ready yet is not a failure — the gallery picks the status up later.
      });
      const refreshed = await loadVideos();
      const uploaded = refreshed.find((v) => v._id === videoId);
      updateItem(phaseId, itemId, {
        companionVideo: videoId,
        companionVideoData: uploaded || { _id: videoId, title, status: "processing" } as VideoOption,
      });
    } catch (err: any) {
      setCompanionError(err?.response?.data?.message || err?.message || "Video upload failed.");
    } finally {
      setCompanionUploading(false);
      setCompanionProgress(0);
    }
  };

  const loadUsers = async () => {
    try {
      setUsersLoading(true);
      const res = await usersAdminAPI.list({ limit: 200, page: 1 });
      setUserOptions((res.data?.data || []).map((u: any) => ({ _id: u._id, name: u.name, email: u.email })));
    } catch {
      // non-critical
    } finally {
      setUsersLoading(false);
    }
  };
  const addPhase = () => {
    const p: Phase = { id: genId(), title: `Phase ${phases.length + 1}`, order: phases.length, items: [], expanded: true };
    setPhases((prev) => [...prev, p]);
    setActivePhaseId(p.id);
    setActiveItemId(null);
    markChanged();
  };

  const removePhase = (phaseId: string) => {
    setPhases((prev) => renumberPhases(prev.filter((p) => p.id !== phaseId)));
    if (activePhaseId === phaseId) { setActivePhaseId(null); setActiveItemId(null); }
    setDeleteConfirm(null);
    markChanged();
  };

  const updatePhase = (phaseId: string, patch: Partial<Phase>) => {
    setPhases((prev) => prev.map((p) => (p.id === phaseId ? { ...p, ...patch } : p)));
    markChanged();
  };

  const updatePhaseTitle = (phaseId: string, t: string) => updatePhase(phaseId, { title: t });

  const togglePhaseExpanded = (phaseId: string) => {
    setPhases((prev) => prev.map((p) => (p.id === phaseId ? { ...p, expanded: !p.expanded } : p)));
  };

  const renumberPhases = (list: Phase[]): Phase[] =>
    list.map((p, i) => {
      const defaultName = /^Phase \d+$/.test(p.title);
      return { ...p, order: i, title: defaultName ? `Phase ${i + 1}` : p.title };
    });

  const movePhase = (phaseId: string, dir: -1 | 1) => {
    setPhases((prev) => {
      const idx = prev.findIndex((p) => p.id === phaseId);
      if (idx < 0) return prev;
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const copy = [...prev];
      [copy[idx], copy[newIdx]] = [copy[newIdx], copy[idx]];
      return renumberPhases(copy);
    });
    markChanged();
  };

  // Item Ops
  const openAddItemDialog = (phaseId: string) => {
    setAddItemPhaseId(phaseId);
    setAddItemType("video");
    setAddItemDialogOpen(true);
  };

  const confirmAddItem = () => {
    const phase = phases.find((p) => p.id === addItemPhaseId);
    if (!phase) return;
    const defaultTitle = addItemType === "video" ? "New Video" : addItemType === "pdf" ? "New PDF" : addItemType === "survey" ? "New Survey" : "New Letter";
    const newItem: PhaseItem = {
      id: genId(),
      type: addItemType,
      title: defaultTitle,
      order: phase.items.length,
      titleAr: "",
      video: "", pdfUrl: "", pdfUrlAr: "", pdfPages: 0, companionVideo: "",
      letterContent: "", letterContentAr: "",
      surveyConfig: addItemType === "survey" ? { mode: "survey", questions: [] } : undefined,
    };
    setPhases((prev) => prev.map((p) => p.id === addItemPhaseId ? { ...p, items: [...p.items, newItem], expanded: true } : p));
    setActivePhaseId(addItemPhaseId);
    setActiveItemId(newItem.id);
    setAddItemDialogOpen(false);
    markChanged();
  };

  const removeItem = (phaseId: string, itemId: string) => {
    setPhases((prev) => prev.map((p) => p.id === phaseId ? { ...p, items: p.items.filter((i) => i.id !== itemId) } : p));
    if (activeItemId === itemId) setActiveItemId(null);
    setDeleteConfirm(null);
    markChanged();
  };

  const moveItem = (phaseId: string, itemId: string, dir: -1 | 1) => {
    setPhases((prev) => prev.map((p) => {
      if (p.id !== phaseId) return p;
      const idx = p.items.findIndex((i) => i.id === itemId);
      if (idx < 0) return p;
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= p.items.length) return p;
      const copy = [...p.items];
      [copy[idx], copy[newIdx]] = [copy[newIdx], copy[idx]];
      return { ...p, items: copy };
    }));
    markChanged();
  };

  const updateItem = (phaseId: string, itemId: string, updates: Partial<PhaseItem>) => {
    setPhases((prev) => prev.map((p) => p.id === phaseId ? { ...p, items: p.items.map((i) => i.id === itemId ? { ...i, ...updates } : i) } : p));
    markChanged();
  };

  const duplicateItem = (phaseId: string, itemId: string) => {
    setPhases((prev) => prev.map((p) => {
      if (p.id !== phaseId) return p;
      const item = p.items.find((i) => i.id === itemId);
      if (!item) return p;
      const dup: PhaseItem = { ...item, id: genId(), title: `${item.title} (copy)`, order: p.items.length };
      return { ...p, items: [...p.items, dup] };
    }));
    markChanged();
  };

  const getActiveItem = (): { phase: Phase; item: PhaseItem } | null => {
    if (!activePhaseId || !activeItemId) return null;
    const phase = phases.find((p) => p.id === activePhaseId);
    if (!phase) return null;
    const item = phase.items.find((i) => i.id === activeItemId);
    if (!item) return null;
    return { phase, item };
  };

  // Uploads
  const handlePdfUpload = async (file: File, phaseId: string, itemId: string) => {
    try {
      setPdfUploading(true);
      const res = await coursesAPI.uploadPdf(file);
      // Store the R2 key (e.g. "pdfs/uuid.pdf") â€" not the signed URL.
      // The proxy generates a fresh signed URL from the key on every view request.
      const pdfKey = res.data.key ?? res.data.url;
      updateItem(phaseId, itemId, { pdfUrl: pdfKey });
    } catch { setError("Failed to upload PDF"); } finally { setPdfUploading(false); }
  };

  const handlePdfUploadAr = async (file: File, phaseId: string, itemId: string) => {
    try {
      setPdfUploadingAr(true);
      const res = await coursesAPI.uploadPdf(file);
      const pdfKey = res.data.key ?? res.data.url;
      updateItem(phaseId, itemId, { pdfUrlAr: pdfKey });
    } catch { setError("Failed to upload Arabic PDF"); } finally { setPdfUploadingAr(false); }
  };

  const handleThumbnailUpload = async (file: File) => {
    try {
      setThumbUploading(true);
      const res = await coursesAPI.uploadThumbnail(file);
      setThumbnail(res.data.url);
      markChanged();
    } catch { setError("Failed to upload thumbnail"); } finally { setThumbUploading(false); }
  };

  // Save
  const handleSave = async () => {
    if (!title.trim()) { setError("Course title is required"); return; }
    try {
      setSaving(true);
      setError("");
      const phasesPayload: CoursePhasePayload[] = phases.map((p, idx) => ({
        title: p.title,
        titleAr: p.titleAr || "",
        order: idx,
        items: p.items.map((item, iIdx) => ({
          type: item.type, title: item.title,
          titleAr: item.titleAr || "",
          video: item.type === "video" ? item.video || undefined : undefined,
          pdfUrl: item.type === "pdf" ? item.pdfUrl || undefined : undefined,
          pdfUrlAr: item.type === "pdf" ? item.pdfUrlAr || undefined : undefined,
          companionVideo: item.type === "pdf" ? item.companionVideo || null : undefined,
          pdfPages: item.type === "pdf" ? item.pdfPages || 0 : undefined,
          letterContent: item.type === "letter" ? item.letterContent || "" : undefined,
          letterContentAr: item.type === "letter" ? item.letterContentAr || "" : undefined,
          surveyConfig: item.type === "survey" ? item.surveyConfig : undefined,
          order: iIdx,
        })),
      }));
      const payload = {
        title, titleAr, description, descriptionAr, thumbnail, status,
        sessionType: (sessionType || null) as "group" | "private" | null,
        assignedUserId: sessionType === "private" ? (assignedUserId || null) : null,
        phases: phasesPayload,
      };
      if (isEditing) {
        await coursesAPI.update(id!, payload);
      } else {
        const res = await coursesAPI.create(payload);
        navigate(`/admin/courses/${res.data._id}`, { replace: true });
      }
      setHasChanges(false);
      setSnackbar({ open: true, message: "Course saved successfully!", severity: "success" });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || "Failed to save course", severity: "error" });
    } finally { setSaving(false); }
  };

  // ===== SIDEBAR =====
  const renderSidebar = () => (
    <Box sx={{ width: PHASE_SIDEBAR_WIDTH, height: "100%", display: "flex", flexDirection: "column", bgcolor: COLORS.sidebar, borderRight: `1px solid ${COLORS.border}` }}>
      {/* Header */}
      <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Timeline sx={{ fontSize: 20, color: COLORS.accent }} />
          <Typography variant="subtitle2" fontWeight={700} color={COLORS.text} letterSpacing={"-0.01em"}>
            Course Phases
          </Typography>
          <Chip label={phases.length} size="small" sx={{ height: 20, fontSize: "0.65rem", bgcolor: COLORS.accentMedium, color: COLORS.accent, fontWeight: 700 }} />
        </Box>
        <Tooltip title="Add Phase" arrow>
          <IconButton size="small" onClick={addPhase} sx={{ bgcolor: COLORS.accentLight, color: COLORS.accent, "&:hover": { bgcolor: COLORS.accentMedium } }}>
            <Add sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Course Info Card */}
      <Box
        onClick={() => { setActivePhaseId(null); setActiveItemId(null); if (isMobile) setSidebarOpen(false); }}
        sx={{
          mx: 2, mt: 2, mb: 1, p: 1.5, borderRadius: "10px", cursor: "pointer",
          bgcolor: !activePhaseId && !activeItemId ? "#EEF4FF" : COLORS.white,
          border: `1px solid ${!activePhaseId && !activeItemId ? COLORS.accent : COLORS.border}`,
          borderLeft: !activePhaseId && !activeItemId ? `3px solid ${COLORS.accent}` : "3px solid transparent",
          transition: "all 0.15s", "&:hover": { borderColor: COLORS.accent },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {thumbnail ? (
            <Avatar src={thumbnail} variant="rounded" sx={{ width: 36, height: 36 }} />
          ) : (
            <Avatar variant="rounded" sx={{ width: 36, height: 36, bgcolor: COLORS.accentLight }}>
              <School sx={{ fontSize: 18, color: COLORS.accent }} />
            </Avatar>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" fontWeight={700} noWrap sx={{ display: "block", color: COLORS.text, fontSize: "0.78rem" }}>
              {title || "Untitled Course"}
            </Typography>
            <Typography variant="caption" sx={{ color: COLORS.textLight, fontSize: "0.68rem" }}>
              Course Details
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Phases List */}
      <Box sx={{ flex: 1, overflow: "auto", py: 1, px: 0.5 }}>
        {phases.length === 0 ? (
          <Box sx={{ px: 3, py: 5, textAlign: "center" }}>
            <FolderOpen sx={{ fontSize: 40, color: "#BBDEFB", mb: 1.5 }} />
            <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ mb: 0.5 }}>
              No phases yet
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Click the + button above to create your first phase
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {phases.map((phase, phaseIdx) => {
              const isActivePhase = activePhaseId === phase.id;
              return (
                <Box key={phase.id} sx={{ mb: 0.5 }}>
                  <Box
                    sx={{
                      mx: 1, borderRadius: "10px", overflow: "hidden",
                      border: isActivePhase && !activeItemId ? `1.5px solid ${COLORS.accent}` : `1.5px solid transparent`,
                      bgcolor: isActivePhase ? COLORS.white : "transparent",
                      boxShadow: isActivePhase ? "0 1px 4px rgba(0,0,0,0.04)" : "none",
                      transition: "all 0.15s",
                    }}
                  >
                    <ListItemButton
                      onClick={() => { setActivePhaseId(phase.id); setActiveItemId(null); togglePhaseExpanded(phase.id); }}
                      sx={{ px: 1.5, py: 1, "&:hover": { bgcolor: "#EEF4FF" } }}
                    >
                      <Box sx={{
                        width: 26, height: 26, borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", mr: 1.5,
                        bgcolor: isActivePhase ? COLORS.accent : "#EEF4FF",
                        color: isActivePhase ? COLORS.white : COLORS.accent,
                        fontWeight: 700, fontSize: "0.72rem", flexShrink: 0,
                      }}>
                        {phaseIdx + 1}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <TextField
                          value={phase.title}
                          onChange={(e) => updatePhaseTitle(phase.id, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          onFocus={(e) => e.stopPropagation()}
                          variant="standard"
                          placeholder="Phase title (English)"
                          InputProps={{ disableUnderline: true, sx: { fontSize: "0.82rem", fontWeight: 600, color: COLORS.text, py: 0 } }}
                          fullWidth
                        />
                        <TextField
                          value={phase.titleAr || ""}
                          onChange={(e) => updatePhase(phase.id, { titleAr: e.target.value })}
                          onClick={(e) => e.stopPropagation()}
                          onFocus={(e) => e.stopPropagation()}
                          variant="standard"
                          placeholder="عنوان المرحلة (اختياري)"
                          InputProps={{
                            disableUnderline: true,
                            sx: {
                              fontSize: "0.78rem", fontWeight: 600, color: COLORS.textMuted, py: 0,
                              direction: "rtl",
                              fontFamily: "'Cairo', 'Segoe UI', sans-serif",
                            },
                          }}
                          fullWidth
                        />
                        <Typography variant="caption" sx={{ color: COLORS.textLight, fontSize: "0.65rem" }}>
                          {phase.items.length} item{phase.items.length !== 1 ? "s" : ""}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); movePhase(phase.id, -1); }} disabled={phaseIdx === 0} sx={{ p: 0.3, opacity: 0.5, "&:hover": { opacity: 1 } }}>
                          <ArrowUpward sx={{ fontSize: 14 }} />
                        </IconButton>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); movePhase(phase.id, 1); }} disabled={phaseIdx === phases.length - 1} sx={{ p: 0.3, opacity: 0.5, "&:hover": { opacity: 1 } }}>
                          <ArrowDownward sx={{ fontSize: 14 }} />
                        </IconButton>
                        {phase.expanded ? <ExpandLess sx={{ fontSize: 18, color: "#9CA3AF" }} /> : <ExpandMore sx={{ fontSize: 18, color: "#9CA3AF" }} />}
                      </Box>
                    </ListItemButton>

                    <Collapse in={phase.expanded} timeout="auto">
                      <Box sx={{ px: 1, pb: 1 }}>
                        {phase.items.map((item, itemIdx) => {
                          const cfg = ITEM_CONFIG[item.type];
                          const isActive = activeItemId === item.id;
                          return (
                            <Box
                              key={item.id}
                              onClick={() => { setActivePhaseId(phase.id); setActiveItemId(item.id); if (isMobile) setSidebarOpen(false); }}
                              sx={{
                                display: "flex", alignItems: "center", gap: 1, py: 0.7, px: 1.5,
                                borderRadius: "8px", cursor: "pointer", mb: 0.3,
                                bgcolor: isActive ? cfg.bg : "transparent",
                                border: isActive ? `1px solid ${cfg.color}30` : "1px solid transparent",
                                transition: "all 0.12s",
                                "&:hover": { bgcolor: isActive ? cfg.bg : "rgba(0,0,0,0.025)" },
                                "&:hover .item-actions": { opacity: 1 },
                              }}
                            >
                              <Box sx={{
                                width: 24, height: 24, borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center",
                                bgcolor: isActive ? `${cfg.color}20` : "rgba(0,0,0,0.04)", color: cfg.color, flexShrink: 0,
                              }}>
                                <cfg.icon sx={{ fontSize: 14 }} />
                              </Box>
                              <Typography variant="caption" noWrap sx={{ flex: 1, fontWeight: isActive ? 600 : 400, fontSize: "0.76rem", color: COLORS.text }}>
                                {item.title}
                              </Typography>
                              <Box className="item-actions" sx={{ display: "flex", gap: 0, opacity: 0, transition: "opacity 0.15s" }}>
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); moveItem(phase.id, item.id, -1); }} disabled={itemIdx === 0} sx={{ p: 0.2 }}>
                                  <ArrowUpward sx={{ fontSize: 12 }} />
                                </IconButton>
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); moveItem(phase.id, item.id, 1); }} disabled={itemIdx === phase.items.length - 1} sx={{ p: 0.2 }}>
                                  <ArrowDownward sx={{ fontSize: 12 }} />
                                </IconButton>
                              </Box>
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDeleteConfirm({ type: "item", phaseId: phase.id, itemId: item.id }); }} sx={{ p: 0.2, opacity: 0.3, "&:hover": { opacity: 1, color: COLORS.error } }}>
                                <Close sx={{ fontSize: 13 }} />
                              </IconButton>
                            </Box>
                          );
                        })}

                        <Box
                          onClick={() => openAddItemDialog(phase.id)}
                          sx={{
                            display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5,
                            py: 0.8, borderRadius: "8px", cursor: "pointer", mt: 0.5,
                            border: `1.5px dashed #1565C0`,
                            transition: "all 0.15s",
                            "&:hover": { borderColor: COLORS.accent, bgcolor: COLORS.accentLight },
                          }}
                        >
                          <Add sx={{ fontSize: 15, color: COLORS.accent }} />
                          <Typography variant="caption" sx={{ color: COLORS.accent, fontWeight: 500, fontSize: "0.72rem" }}>
                            Add Content
                          </Typography>
                        </Box>

                        <Box sx={{ mt: 0.5, textAlign: "right" }}>
                          <Button size="small" onClick={() => setDeleteConfirm({ type: "phase", phaseId: phase.id })} sx={{ color: COLORS.error, fontSize: "0.68rem", textTransform: "none", p: 0, minWidth: 0, "&:hover": { textDecoration: "underline", bgcolor: "transparent" } }}>
                            Delete Phase
                          </Button>
                        </Box>
                      </Box>
                    </Collapse>
                  </Box>
                </Box>
              );
            })}
          </List>
        )}
      </Box>

      {/* Sidebar Footer Stats */}
      <Box sx={{ px: 2.5, py: 1.5, borderTop: `1px solid ${COLORS.border}`, bgcolor: "#F8FBFF" }}>
        <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center" }}>
          {[
            { count: stats.videoCount, color: COLORS.video, label: "Videos" },
            { count: stats.pdfCount, color: COLORS.pdf, label: "PDFs" },
            { count: stats.letterCount, color: COLORS.letter, label: "Letters" },
            { count: stats.surveyCount, color: COLORS_SURVEY, label: "Surveys" },
          ].map((s) => (
            <Tooltip key={s.label} title={s.label} arrow>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: s.color }} />
                <Typography variant="caption" sx={{ fontSize: "0.7rem", color: COLORS.textMuted, fontWeight: 600 }}>
                  {s.count}
                </Typography>
              </Box>
            </Tooltip>
          ))}
        </Box>
      </Box>
    </Box>
  );

  // ===== COURSE DETAILS =====
  const renderCourseDetails = () => (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 800, mx: "auto" }}>
      <Breadcrumbs separator={<NavigateNext sx={{ fontSize: 16 }} />} sx={{ mb: 3 }}>
        <Link underline="hover" sx={{ cursor: "pointer", color: COLORS.textMuted, fontSize: "0.82rem" }} onClick={() => navigate("/admin/courses")}>
          Courses
        </Link>
        <Typography sx={{ fontSize: "0.82rem", color: COLORS.text, fontWeight: 600 }}>
          {isEditing ? "Edit" : "Create"}
        </Typography>
      </Breadcrumbs>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {[
          { label: "Phases", value: stats.phaseCount, icon: <Timeline />, color: COLORS.accent },
          { label: "Total Items", value: stats.totalItems, icon: <FolderOpen />, color: "#6366F1" },
          { label: "Videos", value: stats.videoCount, icon: <PlayCircle />, color: COLORS.video },
          { label: "Documents", value: stats.pdfCount + stats.letterCount, icon: <Description />, color: COLORS.letter },
        ].map((card) => (
          <Grid size={{ xs: 6, sm: 3 }} key={card.label}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: "12px", border: `1px solid ${COLORS.border}`, textAlign: "center", transition: "all 0.15s", "&:hover": { borderColor: card.color, boxShadow: `0 4px 20px ${card.color}20`, transform: "translateY(-2px)" } }}>
              <Box sx={{ color: card.color, mb: 0.5 }}>{card.icon}</Box>
              <Typography variant="h5" fontWeight={800} sx={{ color: COLORS.text }}>{card.value}</Typography>
              <Typography variant="caption" sx={{ color: COLORS.textMuted, fontSize: "0.72rem" }}>{card.label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Form */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: "14px", border: `1px solid ${COLORS.border}`, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2.5, color: COLORS.text }}>
          Course Information
        </Typography>

        <BilingualField
          label="Course Title"
          valueEn={title}
          valueAr={titleAr}
          onChangeEn={(v) => { setTitle(v); markChanged(); }}
          onChangeAr={(v) => { setTitleAr(v); markChanged(); }}
          placeholderEn="e.g. Dr.Paw Complete Masterclass"
          placeholderAr="مثال: دورة دكتور باو الشاملة"
          startAdornment={<School sx={{ mr: 1, color: COLORS.textLight, fontSize: 20 }} />}
          sx={{ mb: 2.5 }}
        />

        <BilingualField
          label="Description"
          valueEn={description}
          valueAr={descriptionAr}
          onChangeEn={(v) => { setDescription(v); markChanged(); }}
          onChangeAr={(v) => { setDescriptionAr(v); markChanged(); }}
          placeholderEn="Describe what students will learn in this course..."
          placeholderAr="صف ما سيتعلمه الطلاب في هذه الدورة..."
          multiline
          rows={4}
          sx={{ mb: 2.5 }}
        />

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={status} label="Status"
                onChange={(e: SelectChangeEvent) => { setStatus(e.target.value); markChanged(); }}
                sx={{ borderRadius: "10px" }}
              >
                <MenuItem value="draft">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: COLORS.warning }} /> Draft
                  </Box>
                </MenuItem>
                <MenuItem value="published">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: COLORS.success }} /> Published
                  </Box>
                </MenuItem>
                <MenuItem value="archived">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#9CA3AF" }} /> Archived
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Session Type</InputLabel>
              <Select
                value={sessionType} label="Session Type"
                onChange={(e: SelectChangeEvent) => { setSessionType(e.target.value); markChanged(); }}
                sx={{ borderRadius: "10px" }}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="group">Group Session</MenuItem>
                <MenuItem value="private">Private Session</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {sessionType === "private" && (
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Assign to User</InputLabel>
                <Select
                  value={assignedUserId}
                  label="Assign to User"
                  onChange={(e: SelectChangeEvent) => { setAssignedUserId(e.target.value); markChanged(); }}
                  sx={{ borderRadius: "10px" }}
                  disabled={usersLoading}
                >
                  <MenuItem value=""><em>No user assigned</em></MenuItem>
                  {userOptions.map((u) => (
                    <MenuItem key={u._id} value={u._id}>{u.name} ({u.email})</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: "block", color: COLORS.textMuted }}>
              Thumbnail
            </Typography>
            {thumbnail ? (
              <Box sx={{ position: "relative", display: "inline-block" }}>
                <Box component="img" src={thumbnail} sx={{ width: "100%", maxWidth: 220, height: 100, borderRadius: "10px", objectFit: "cover", border: `1px solid ${COLORS.border}` }} />
                <IconButton size="small" onClick={() => { setThumbnail(""); markChanged(); }} sx={{ position: "absolute", top: -6, right: -6, bgcolor: COLORS.error, color: "#fff", "&:hover": { bgcolor: "#DC2626" }, width: 22, height: 22 }}>
                  <Close sx={{ fontSize: 12 }} />
                </IconButton>
              </Box>
            ) : (
              <Box
                onClick={() => thumbInputRef.current?.click()}
                sx={{
                  height: 100, borderRadius: "10px", border: `2px dashed ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", cursor: "pointer",
                  transition: "all 0.15s", "&:hover": { borderColor: COLORS.accent, bgcolor: COLORS.accentLight },
                }}
              >
                {thumbUploading ? (
                  <CircularProgress size={24} sx={{ color: COLORS.accent }} />
                ) : (
                  <>
                    <CloudUpload sx={{ fontSize: 24, color: COLORS.textLight, mb: 0.3 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>Upload Image</Typography>
                  </>
                )}
              </Box>
            )}
            <input ref={thumbInputRef} type="file" hidden accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleThumbnailUpload(f); }} />
          </Grid>
        </Grid>
      </Paper>

      {/* Getting Started or Course Structure */}
      {phases.length === 0 ? (
        <Paper elevation={0} sx={{ p: 4, borderRadius: "14px", border: `1px solid ${COLORS.border}`, textAlign: "center", bgcolor: COLORS.accentLight }}>
          <AutoAwesome sx={{ fontSize: 40, color: COLORS.accent, mb: 1.5 }} />
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: COLORS.text }}>
            Ready to build your course?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: "auto" }}>
            Start by adding phases to organize your content. Each phase can contain videos, PDFs, and written content.
          </Typography>
          <Button
            variant="contained" startIcon={<Add />} onClick={addPhase}
            sx={{ bgcolor: COLORS.accent, "&:hover": { bgcolor: COLORS.accentDark }, borderRadius: "10px", textTransform: "none", fontWeight: 600, px: 4 }}
          >
            Add First Phase
          </Button>
        </Paper>
      ) : (
        <Paper elevation={0} sx={{ p: 3, borderRadius: "14px", border: `1px solid ${COLORS.border}` }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, color: COLORS.text }}>
            Course Structure
          </Typography>
          {phases.map((phase, idx) => (
            <Box key={phase.id} sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2, "&:last-child": { mb: 0 } }}>
              <Box sx={{ width: 32, height: 32, borderRadius: "10px", bgcolor: COLORS.accent, color: COLORS.white, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "0.8rem", flexShrink: 0, mt: 0.3 }}>
                {idx + 1}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={600} sx={{ color: COLORS.text }}>{phase.title}</Typography>
                <Box sx={{ display: "flex", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
                  {phase.items.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">No items yet</Typography>
                  ) : (
                    phase.items.map((item) => {
                      const cfg = ITEM_CONFIG[item.type];
                      return (
                        <Chip
                          key={item.id} size="small" label={truncate(item.title, 20)}
                          icon={<cfg.icon sx={{ fontSize: "14px !important" }} />}
                          sx={{ height: 24, fontSize: "0.68rem", bgcolor: cfg.bg, color: cfg.color, fontWeight: 500, "& .MuiChip-icon": { color: cfg.color } }}
                        />
                      );
                    })
                  )}
                </Box>
              </Box>
            </Box>
          ))}
        </Paper>
      )}
    </Box>
  );

  // ===== VIDEO EDITOR =====
  const renderVideoEditor = (phase: Phase, item: PhaseItem) => (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 800, mx: "auto" }}>
      <Breadcrumbs separator={<NavigateNext sx={{ fontSize: 16 }} />} sx={{ mb: 3 }}>
        <Link underline="hover" sx={{ cursor: "pointer", color: COLORS.textMuted, fontSize: "0.82rem" }} onClick={() => setActiveItemId(null)}>
          {phase.title}
        </Link>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <VideoLibrary sx={{ fontSize: 16, color: COLORS.video }} />
          <Typography sx={{ fontSize: "0.82rem", color: COLORS.text, fontWeight: 600 }}>Video</Typography>
        </Box>
      </Breadcrumbs>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: "10px", bgcolor: COLORS.videoBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <VideoLibrary sx={{ color: COLORS.video, fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={700} color={COLORS.text}>Video Content</Typography>
          <Typography variant="caption" color="text.secondary">Select a video from your library</Typography>
        </Box>
        <Tooltip title="Duplicate" arrow>
          <IconButton size="small" onClick={() => duplicateItem(phase.id, item.id)} sx={{ color: COLORS.textMuted }}>
            <ContentCopy sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Paper elevation={0} sx={{ p: 2.5, borderRadius: "12px", border: `1px solid ${COLORS.border}`, mb: 2.5 }}>
        <BilingualField
          label="Item Title"
          valueEn={item.title}
          valueAr={item.titleAr || ""}
          onChangeEn={(v) => updateItem(phase.id, item.id, { title: v })}
          onChangeAr={(v) => updateItem(phase.id, item.id, { titleAr: v })}
          helperText="Shown to students in the course outline"
        />
      </Paper>

      <Paper elevation={0} sx={{ p: 2.5, borderRadius: "12px", border: `1px solid ${COLORS.border}`, mb: 2.5 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>Select Video</Typography>
        {videosLoadError && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {videosLoadError}
          </Alert>
        )}
        <Autocomplete
          options={availableVideos}
          getOptionLabel={(opt) => opt.title}
          value={availableVideos.find((v) => v._id === item.video) || item.videoData || null}
          onChange={(_, value) => updateItem(phase.id, item.id, { video: value?._id || "", videoData: value || undefined, title: value?.title || item.title })}
          loading={videosLoading}
          noOptionsText={videosLoading ? "Loading videos..." : "No videos found in video gallery"}
          renderOption={(props, option) => (
            <Box component="li" {...props} sx={{ display: "flex", gap: 1.5, py: 1.2, "&:hover": { bgcolor: `${COLORS.video}08 !important` } }}>
              <Box sx={{ width: 72, height: 44, borderRadius: "8px", overflow: "hidden", bgcolor: COLORS.videoBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {option.thumbnailUrl ? (
                  <Box component="img" src={option.thumbnailUrl} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <VideoLibrary sx={{ fontSize: 22, color: COLORS.video }} />
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>{option.title}</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">{formatDuration(option.duration)}</Typography>
                  <Chip label={option.status} size="small" sx={{ height: 18, fontSize: "0.6rem", bgcolor: option.status === "ready" ? "#ECFDF5" : "rgba(245,158,11,0.1)", color: option.status === "ready" ? COLORS.success : COLORS.warning }} />
                </Box>
              </Box>
            </Box>
          )}
          renderInput={(params) => (
            <TextField {...params} placeholder="Search your video library..." sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
          )}
        />
      </Paper>

      {(item.videoData?.thumbnailUrl || item.video) && (
        <Paper elevation={0} sx={{ borderRadius: "12px", border: `1px solid ${COLORS.border}`, overflow: "hidden" }}>
          <Box
            sx={{ position: "relative", cursor: "pointer" }}
            onClick={() => item.videoData && setVideoPreview(item.videoData)}
          >
            <Box component="img" src={item.videoData?.thumbnailUrl || ""} sx={{ width: "100%", height: 280, objectFit: "cover", bgcolor: "#1a1a1a" }} />
            <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(0,0,0,0.25)" }}>
              <Box sx={{ width: 56, height: 56, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.9)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <PlayCircle sx={{ fontSize: 36, color: COLORS.video }} />
              </Box>
            </Box>
          </Box>
          <Box sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: COLORS.text }}>{item.videoData?.title || "Selected Video"}</Typography>
            <Box sx={{ display: "flex", gap: 2, mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Duration: {formatDuration(item.videoData?.duration || 0)}</Typography>
              <Chip label="Ready" size="small" sx={{ height: 20, fontSize: "0.65rem", bgcolor: "#ECFDF5", color: COLORS.success }} />
            </Box>
            <Button
              size="small"
              startIcon={<PlayCircle />}
              sx={{ mt: 1.2, textTransform: "none", borderRadius: "8px" }}
              onClick={() => item.videoData && setVideoPreview(item.videoData)}
              disabled={!item.videoData}
            >
              Open Video
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );

  // ===== PDF EDITOR =====
  const renderPdfEditor = (phase: Phase, item: PhaseItem) => {
    const activePdfUrl = pdfEditorLang === "en" ? item.pdfUrl : item.pdfUrlAr;
    const isUploading  = pdfEditorLang === "en" ? pdfUploading : pdfUploadingAr;
    const inputRef     = pdfEditorLang === "en" ? pdfInputRef  : pdfInputArRef;

    const pdfUploadZone = !activePdfUrl ? (
      <Box
        onClick={() => inputRef.current?.click()}
        sx={{
          flex: "1 1 auto", minHeight: 240, borderRadius: "16px",
          border: `2px dashed ${isUploading ? COLORS.pdf : "#E8EDF5"}`,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          cursor: isUploading ? "default" : "pointer",
          transition: "all 0.25s",
          background: isUploading ? "rgba(239,68,68,0.02)" : "linear-gradient(135deg,#FAFBFF 0%,#F8F9FF 100%)",
          "&:hover": isUploading ? {} : {
            borderColor: COLORS.pdf, background: "rgba(239,68,68,0.03)",
            "& .upload-icon-box": { transform: "scale(1.06)", boxShadow: "0 8px 24px rgba(239,68,68,0.2)" },
          },
        }}
      >
        {isUploading ? (
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress size={40} sx={{ color: COLORS.pdf, mb: 1.5 }} />
            <Typography fontWeight={600} sx={{ color: COLORS.text, mb: 0.5 }}>Uploading...</Typography>
          </Box>
        ) : (
          <Box sx={{ textAlign: "center", px: 3 }}>
            <Box className="upload-icon-box" sx={{
              width: 68, height: 68, borderRadius: "18px", mx: "auto", mb: 2,
              background: "linear-gradient(135deg,#EF4444,#F87171)",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 6px 20px rgba(239,68,68,0.25)",
              transition: "all 0.3s cubic-bezier(.34,1.56,.64,1)",
            }}>
              <CloudUpload sx={{ fontSize: 32, color: "#fff" }} />
            </Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: COLORS.text, mb: 0.5 }}>
              Drop your {pdfEditorLang === "ar" ? "Arabic" : "English"} PDF here
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.textMuted, mb: 1.5 }}>or click to browse</Typography>
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.6, px: 1.5, py: 0.6, borderRadius: "20px", bgcolor: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)" }}>
              <PictureAsPdf sx={{ fontSize: 13, color: COLORS.pdf }} />
              <Typography variant="caption" fontWeight={700} sx={{ color: COLORS.pdf }}>PDF only · Max 50MB</Typography>
            </Box>
          </Box>
        )}
      </Box>
    ) : (
      <Box sx={{ flex: "1 1 auto", display: "flex", flexDirection: "column" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 1.5, px: 2, py: 1.2, borderRadius: "10px", bgcolor: "#ECFDF5", border: "1px solid rgba(16,185,129,0.2)", flexShrink: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
            <CheckCircle sx={{ fontSize: 18, color: COLORS.success }} />
            <Typography variant="body2" fontWeight={700} sx={{ color: "#065F46" }}>
              {pdfEditorLang === "ar" ? "Arabic" : "English"} PDF uploaded
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button size="small" variant="outlined"
              onClick={() => updateItem(phase.id, item.id, pdfEditorLang === "en" ? { pdfUrl: "" } : { pdfUrlAr: "" })}
              sx={{ textTransform: "none", fontSize: "0.75rem", borderRadius: "18px", color: COLORS.error, borderColor: "rgba(239,68,68,0.3)", "&:hover": { bgcolor: "rgba(239,68,68,0.06)", borderColor: COLORS.error } }}>
              Remove
            </Button>
            <Button size="small" variant="outlined" startIcon={<CloudUpload sx={{ fontSize: "13px !important" }} />}
              onClick={() => inputRef.current?.click()}
              sx={{ textTransform: "none", fontSize: "0.75rem", borderRadius: "18px", color: COLORS.pdf, borderColor: "rgba(239,68,68,0.3)", "&:hover": { bgcolor: "rgba(239,68,68,0.06)", borderColor: COLORS.pdf } }}>
              Replace
            </Button>
          </Box>
        </Box>
        <Box sx={{ flex: "1 1 auto", minHeight: { xs: 0, md: 560 } }}>
          <PdfPreview pdfKey={activePdfUrl || ""} title={item.title} />
        </Box>
      </Box>
    );

    return (
      <Box sx={{ p: { xs: 2, md: 4 }, minHeight: "100%", display: "flex", flexDirection: "column", maxWidth: 1100, mx: "auto" }}>
        {/* Breadcrumb */}
        <Breadcrumbs separator={<NavigateNext sx={{ fontSize: 14 }} />} sx={{ mb: 3 }}>
          <Link underline="hover" sx={{ cursor: "pointer", color: COLORS.textMuted, fontSize: "0.8rem" }} onClick={() => setActiveItemId(null)}>
            {phase.title}
          </Link>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <PictureAsPdf sx={{ fontSize: 14, color: COLORS.pdf }} />
            <Typography sx={{ fontSize: "0.8rem", color: COLORS.text, fontWeight: 600 }}>PDF Document</Typography>
          </Box>
        </Breadcrumbs>

        {/* Header card */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5, p: 2, borderRadius: "14px", background: "linear-gradient(135deg,#FEF2F2 0%,#FFF5F5 100%)", border: "1px solid rgba(239,68,68,0.15)" }}>
          <Box sx={{ width: 44, height: 44, borderRadius: "12px", background: "linear-gradient(135deg,#EF4444,#F87171)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 12px rgba(239,68,68,0.3)", flexShrink: 0 }}>
            <PictureAsPdf sx={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={800} color={COLORS.text}>PDF Document</Typography>
            <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Upload English + Arabic PDFs — members see the one that matches their language</Typography>
          </Box>
          <Tooltip title="Duplicate item" arrow>
            <IconButton size="small" onClick={() => duplicateItem(phase.id, item.id)} sx={{ color: COLORS.textMuted, bgcolor: "rgba(0,0,0,0.04)", "&:hover": { bgcolor: "rgba(0,0,0,0.08)" } }}>
              <ContentCopy sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Title */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" fontWeight={700} sx={{ color: COLORS.textMuted, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: "0.7rem", mb: 0.8, display: "block" }}>Document Title</Typography>
          <BilingualField
            label="Document Title"
            valueEn={item.title}
            valueAr={item.titleAr || ""}
            onChangeEn={(v) => updateItem(phase.id, item.id, { title: v })}
            onChangeAr={(v) => updateItem(phase.id, item.id, { titleAr: v })}
            placeholderEn="e.g. Pre-Op Checklist, Module 1 Guide..."
            placeholderAr="مثال: قائمة ما قبل العملية، دليل الوحدة الأولى..."
          />
        </Box>

        {/* Language toggle + status pills */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          {(["en", "ar"] as const).map((lang) => {
            const hasFile = lang === "en" ? !!item.pdfUrl : !!item.pdfUrlAr;
            const isActive = pdfEditorLang === lang;
            return (
              <Box
                key={lang}
                onClick={() => setPdfEditorLang(lang)}
                sx={{
                  display: "flex", alignItems: "center", gap: 0.8,
                  px: 2, py: 0.8, borderRadius: "20px", cursor: "pointer",
                  border: `2px solid ${isActive ? COLORS.accent : COLORS.border}`,
                  bgcolor: isActive ? COLORS.accentLight : COLORS.white,
                  transition: "all 0.15s",
                  "&:hover": { borderColor: COLORS.accent },
                }}
              >
                <Typography variant="caption" fontWeight={700} sx={{ color: isActive ? COLORS.accent : COLORS.textMuted, fontSize: "0.8rem" }}>
                  {lang === "en" ? "English" : "Arabic (عربي)"}
                </Typography>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: hasFile ? COLORS.success : "#D1D5DB" }} />
              </Box>
            );
          })}
          <Typography variant="caption" sx={{ color: COLORS.textMuted, ml: "auto", fontSize: "0.72rem" }}>
            Arabic is optional — falls back to English if not uploaded
          </Typography>
        </Box>

        {/* Upload / Preview for selected language */}
        {pdfUploadZone}

        {/* Companion video — the paid add-on that goes with this leaflet.
            Buyers who skip the add-on still get the PDF; this is what stays
            locked for them, so leaving it empty simply means no upsell. */}
        {(() => {
          const picked =
            availableVideos.find((v) => v._id === item.companionVideo) ||
            item.companionVideoData ||
            null;

          return (
            <Paper
              elevation={0}
              sx={{ p: 2.5, borderRadius: "12px", border: `1px solid ${COLORS.border}`, mt: 3, flexShrink: 0 }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
                <PlayCircle sx={{ fontSize: 18, color: COLORS.video }} />
                <Typography variant="subtitle2" fontWeight={600}>Companion video</Typography>
                <Chip
                  label="Paid add-on"
                  size="small"
                  sx={{ height: 20, fontSize: "0.68rem", fontWeight: 700, bgcolor: COLORS.videoBg, color: COLORS.video }}
                />
              </Box>
              <Typography variant="caption" sx={{ color: COLORS.textMuted, display: "block", mb: 2 }}>
                Optional. Buyers pay extra to unlock this video alongside the leaflet.
              </Typography>

              {companionError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCompanionError("")}>
                  {companionError}
                </Alert>
              )}

              {companionUploading ? (
                /* ── Uploading ── */
                <Box sx={{ p: 3, borderRadius: "12px", bgcolor: COLORS.videoBg, textAlign: "center" }}>
                  <CloudUpload sx={{ fontSize: 30, color: COLORS.video, mb: 1 }} />
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>
                    Uploading video… {companionProgress}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={companionProgress}
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>
              ) : picked ? (
                /* ── Attached ── */
                <Box
                  sx={{
                    display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap",
                    p: 1.5, borderRadius: "12px",
                    border: `1px solid ${COLORS.border}`, bgcolor: COLORS.white,
                  }}
                >
                  <Box
                    onClick={() => setVideoPreview(picked)}
                    title="Preview video"
                    sx={{
                      width: 104, height: 62, borderRadius: "8px", overflow: "hidden", flexShrink: 0,
                      bgcolor: COLORS.videoBg, cursor: "pointer", position: "relative",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    {picked.thumbnailUrl && !brokenThumbs[picked._id] ? (
                      <Box
                        component="img"
                        src={picked.thumbnailUrl}
                        alt=""
                        // A poster that 404s (or an expired one) falls back to
                        // the icon instead of a broken-image placeholder.
                        onError={() => setBrokenThumbs((prev) => ({ ...prev, [picked._id]: true }))}
                        sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <VideoLibrary sx={{ fontSize: 24, color: COLORS.video }} />
                    )}
                    <PlayCircle
                      sx={{
                        position: "absolute", fontSize: 26, color: "#fff",
                        filter: "drop-shadow(0 1px 3px rgba(0,0,0,.5))",
                      }}
                    />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 160 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ color: COLORS.text }}>
                      {picked.title}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.3, flexWrap: "wrap" }}>
                      {!!picked.duration && (
                        <Typography variant="caption" sx={{ color: COLORS.textMuted }}>
                          {formatDuration(picked.duration)}
                        </Typography>
                      )}
                      <Chip
                        label={picked.status || "ready"}
                        size="small"
                        sx={{
                          height: 18, fontSize: "0.65rem", fontWeight: 700, textTransform: "capitalize",
                          bgcolor: picked.status === "ready" ? `${COLORS.success}18` : `${COLORS.video}18`,
                          color: picked.status === "ready" ? COLORS.success : COLORS.video,
                        }}
                      />
                    </Box>
                  </Box>

                  <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                    <Button
                      size="small"
                      startIcon={<CloudUpload sx={{ fontSize: 16 }} />}
                      onClick={() => companionInputRef.current?.click()}
                      sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                      Replace
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      onClick={() =>
                        updateItem(phase.id, item.id, { companionVideo: "", companionVideoData: undefined })
                      }
                      sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                      Remove
                    </Button>
                  </Stack>
                </Box>
              ) : (
                /* ── Empty: upload here, or reuse something already in the gallery ── */
                <>
                  <Box
                    onClick={() => companionInputRef.current?.click()}
                    sx={{
                      p: 3, borderRadius: "12px", textAlign: "center", cursor: "pointer",
                      border: `2px dashed ${COLORS.border}`,
                      transition: "all 0.15s",
                      "&:hover": { borderColor: COLORS.video, bgcolor: COLORS.videoBg },
                    }}
                  >
                    <CloudUpload sx={{ fontSize: 30, color: COLORS.video, mb: 0.5 }} />
                    <Typography variant="body2" fontWeight={700} sx={{ color: COLORS.text }}>
                      Upload a video
                    </Typography>
                    <Typography variant="caption" sx={{ color: COLORS.textMuted }}>
                      MP4, MOV or WebM — goes straight to the video gallery
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }}>
                    <Typography variant="caption" sx={{ color: COLORS.textMuted }}>
                      or pick one already uploaded
                    </Typography>
                  </Divider>

                  <Autocomplete
                    options={availableVideos}
                    getOptionLabel={(opt) => opt.title}
                    value={null}
                    onChange={(_, value) =>
                      updateItem(phase.id, item.id, {
                        companionVideo: value?._id || "",
                        companionVideoData: value || undefined,
                      })
                    }
                    loading={videosLoading}
                    isOptionEqualToValue={(opt, val) => opt._id === val._id}
                    noOptionsText={
                      videosLoading ? "Loading videos…" : "Nothing in the gallery yet — upload above"
                    }
                    renderInput={(params) => (
                      <TextField {...params} size="small" placeholder="Search the video gallery" />
                    )}
                  />
                </>
              )}

              <input
                ref={companionInputRef}
                type="file"
                hidden
                accept="video/*"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleCompanionUpload(f, phase.id, item.id);
                  e.target.value = "";
                }}
              />
            </Paper>
          );
        })()}

        {/* Hidden file inputs */}
        <input ref={pdfInputRef} type="file" hidden accept=".pdf,application/pdf"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) handlePdfUpload(f, phase.id, item.id); }} />
        <input ref={pdfInputArRef} type="file" hidden accept=".pdf,application/pdf"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) handlePdfUploadAr(f, phase.id, item.id); }} />
      </Box>
    );
  };

  // ===== LETTER EDITOR =====
  const renderLetterEditor = (phase: Phase, item: PhaseItem) => {
    const isArabicBody = letterEditorLang === "ar";
    const activeContent = (isArabicBody ? item.letterContentAr : item.letterContent) || "";
    const setActiveContent = (html: string) =>
      updateItem(phase.id, item.id, isArabicBody ? { letterContentAr: html } : { letterContent: html });
    const charCount = getPlainTextFromHtml(activeContent).length;
    const hasContent = charCount > 0;

    return (
    <Box sx={{ p: { xs: 2, md: 4 }, height: "100%", display: "flex", flexDirection: "column", maxWidth: 1000, mx: "auto" }}>

      {/* Breadcrumb */}
      <Breadcrumbs separator={<NavigateNext sx={{ fontSize: 14 }} />} sx={{ mb: 4 }}>
        <Link underline="hover" sx={{ cursor: "pointer", color: COLORS.textMuted, fontSize: "0.8rem" }} onClick={() => setActiveItemId(null)}>
          {phase.title}
        </Link>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <TextFields sx={{ fontSize: 14, color: COLORS.letter }} />
          <Typography sx={{ fontSize: "0.8rem", color: COLORS.text, fontWeight: 600 }}>Written Content</Typography>
        </Box>
      </Breadcrumbs>

      {/* Header card */}
      <Box sx={{
        display: "flex", alignItems: "center", gap: 2, mb: 3,
        p: 2.5, borderRadius: "16px",
        background: "linear-gradient(135deg, #E0F2F1 0%, #F0FDFA 100%)",
        border: "1px solid rgba(0,137,123,0.18)",
      }}>
        <Box sx={{
          width: 48, height: 48, borderRadius: "14px",
          background: "linear-gradient(135deg, #00897B, #26A69A)",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 4px 12px rgba(0,137,123,0.3)", flexShrink: 0,
        }}>
          <TextFields sx={{ color: "#fff", fontSize: 22 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={800} color={COLORS.text} letterSpacing="-0.01em">
            Written Content
          </Typography>
          <Typography variant="caption" sx={{ color: COLORS.textMuted }}>
            Compose a letter, note, or instructional text for your students
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {hasContent && (
            <Chip
              label={`${charCount} chars`}
              size="small"
              sx={{ height: 24, fontSize: "0.7rem", fontWeight: 700, bgcolor: "rgba(0,137,123,0.1)", color: COLORS.letter }}
            />
          )}
          <Tooltip title="Duplicate item" arrow>
            <IconButton size="small" onClick={() => duplicateItem(phase.id, item.id)}
              sx={{ color: COLORS.textMuted, bgcolor: "rgba(0,0,0,0.04)", "&:hover": { bgcolor: "rgba(0,0,0,0.08)" } }}>
              <ContentCopy sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Title field */}
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="caption" fontWeight={700} sx={{ color: COLORS.textMuted, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: "0.7rem", mb: 0.8, display: "block" }}>
          Content Title
        </Typography>
        <BilingualField
          label="Content Title"
          valueEn={item.title}
          valueAr={item.titleAr || ""}
          onChangeEn={(v) => updateItem(phase.id, item.id, { title: v })}
          onChangeAr={(v) => updateItem(phase.id, item.id, { titleAr: v })}
          placeholderEn="e.g. Welcome Letter, Module Overview, Instructions..."
          placeholderAr="مثال: رسالة ترحيب، نظرة عامة على الوحدة، تعليمات..."
        />
      </Box>

      {/* Language toggle — each language keeps its own body, Arabic optional */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        {(["en", "ar"] as const).map((lang) => {
          const hasBody = getPlainTextFromHtml(
            (lang === "en" ? item.letterContent : item.letterContentAr) || ""
          ).length > 0;
          const isActive = letterEditorLang === lang;
          return (
            <Box
              key={lang}
              onClick={() => setLetterEditorLang(lang)}
              sx={{
                display: "flex", alignItems: "center", gap: 0.8,
                px: 2, py: 0.8, borderRadius: "20px", cursor: "pointer",
                border: `2px solid ${isActive ? COLORS.letter : COLORS.border}`,
                bgcolor: isActive ? "#E0F2F1" : COLORS.white,
                transition: "all 0.15s",
                "&:hover": { borderColor: COLORS.letter },
              }}
            >
              <Typography variant="caption" fontWeight={700} sx={{ color: isActive ? COLORS.letter : COLORS.textMuted, fontSize: "0.8rem" }}>
                {lang === "en" ? "English" : "Arabic (عربي)"}
              </Typography>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: hasBody ? COLORS.success : "#D1D5DB" }} />
            </Box>
          );
        })}
        <Typography variant="caption" sx={{ color: COLORS.textMuted, ml: "auto", fontSize: "0.72rem" }}>
          Arabic is optional — falls back to English if left empty
        </Typography>
      </Box>

      {/* Editor + Preview side by side */}
      <Grid container spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        {/* Editor */}
        <Grid size={{ xs: 12, md: 6 }} sx={{ display: "flex", flexDirection: "column" }}>
          <Box sx={{
            flex: 1, display: "flex", flexDirection: "column",
            borderRadius: "16px", border: `1px solid ${COLORS.border}`,
            overflow: "hidden", bgcolor: COLORS.white,
            boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
          }}>
            {/* Editor header */}
            <Box sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              px: 2.5, py: 1.5,
              borderBottom: `1px solid ${COLORS.border}`,
              bgcolor: "#F8FBFF",
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: COLORS.letter }} />
                <Typography variant="caption" fontWeight={700} sx={{ color: COLORS.text, letterSpacing: "0.04em", textTransform: "uppercase", fontSize: "0.68rem" }}>
                  {isArabicBody ? "Arabic Editor" : "English Editor"}
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: charCount > 0 ? COLORS.letter : COLORS.textMuted, fontWeight: 600, fontSize: "0.72rem" }}>
                {charCount > 0 ? `${charCount.toLocaleString()} characters` : "Start writing..."}
              </Typography>
            </Box>
            <Box
              sx={{ flex: 1, p: 0, direction: isArabicBody ? "rtl" : "ltr" }}
              dir={isArabicBody ? "rtl" : "ltr"}
            >
              <RichTextEditor
                key={`${item.id}-${letterEditorLang}`}
                value={activeContent}
                onChange={setActiveContent}
                placeholder={
                  isArabicBody
                    ? "عزيزي الطالب،\n\nمرحباً بك في هذه المرحلة من رحلتك التعليمية...\n\nاكتب المحتوى هنا."
                    : "Dear Student,\n\nWelcome to this phase of your learning journey...\n\nWrite your content here."
                }
                minHeight={400}
              />
            </Box>
          </Box>
        </Grid>

        {/* Preview */}
        <Grid size={{ xs: 12, md: 6 }} sx={{ display: "flex", flexDirection: "column" }}>
          <Box sx={{
            flex: 1, display: "flex", flexDirection: "column",
            borderRadius: "16px", border: `1px solid ${COLORS.border}`,
            overflow: "hidden",
            boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
          }}>
            {/* Preview header */}
            <Box sx={{
              display: "flex", alignItems: "center", gap: 1,
              px: 2.5, py: 1.5,
              borderBottom: `1px solid ${COLORS.border}`,
              bgcolor: "#F8FBFF",
            }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: hasContent ? "#10B981" : "#E5E7EB" }} />
              <Typography variant="caption" fontWeight={700} sx={{ color: COLORS.text, letterSpacing: "0.04em", textTransform: "uppercase", fontSize: "0.68rem" }}>
                Live Preview
              </Typography>
              {hasContent && (
                <Chip label="Live" size="small" sx={{ height: 18, fontSize: "0.6rem", fontWeight: 700, bgcolor: "#ECFDF5", color: "#10B981", ml: "auto" }} />
              )}
            </Box>

            {hasContent ? (
              <Box sx={{ flex: 1, overflow: "auto", p: 3, bgcolor: "#FDFEFF" }}>
                {/* Letter paper effect */}
                <Box sx={{
                  maxWidth: "68ch", mx: "auto",
                  p: 3, borderRadius: "12px",
                  bgcolor: COLORS.white,
                  boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
                  borderTop: `3px solid ${COLORS.letter}`,
                }}>
                  <Box
                    dir={isArabicBody ? "rtl" : "ltr"}
                    sx={{
                      fontFamily: isArabicBody
                        ? "'Cairo', 'Segoe UI', sans-serif"
                        : "'Georgia', 'Times New Roman', serif",
                      fontSize: "0.95rem",
                      lineHeight: 1.85,
                      color: COLORS.text,
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                      "& p": { my: 0, mb: 1.2 },
                      "& p:last-child": { mb: 0 },
                      "& ul, & ol": { pl: 2.4, my: 0, mb: 1.2 },
                      "& li": { mb: 0.4 },
                      "& a": { color: COLORS.accent, textDecoration: "underline" },
                      "& strong": { fontWeight: 700 },
                      "& h1, & h2, & h3": { lineHeight: 1.4, mb: 1, fontFamily: "inherit" },
                    }}
                    dangerouslySetInnerHTML={{ __html: activeContent }}
                  />
                </Box>
              </Box>
            ) : (
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 4, bgcolor: "#FDFEFF" }}>
                <Box sx={{
                  width: 64, height: 64, borderRadius: "18px", mb: 2,
                  bgcolor: "#F0FDFA", display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Article sx={{ fontSize: 32, color: COLORS.letter, opacity: 0.4 }} />
                </Box>
                <Typography variant="body2" fontWeight={600} sx={{ color: COLORS.textMuted, mb: 0.5 }}>
                  Nothing to preview yet
                </Typography>
                <Typography variant="caption" sx={{ color: COLORS.textMuted, textAlign: "center" }}>
                  Start writing in the editor and your content will appear here in real-time
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
  };

  // ===== SURVEY EDITOR =====
  const renderSurveyEditor = (phase: Phase, item: PhaseItem) => {
    const config: SurveyConfig = item.surveyConfig ?? { mode: "survey", questions: [] };
    const isQuiz = config.mode === "quiz";

    const updateConfig = (updates: Partial<SurveyConfig>) =>
      updateItem(phase.id, item.id, { surveyConfig: { ...config, ...updates } });

    const updateQuestion = (qId: string, updates: Partial<SurveyQuestion>) =>
      updateConfig({ questions: config.questions.map((q) => (q.id === qId ? { ...q, ...updates } : q)) });

    const addQuestion = () => {
      const newQ: SurveyQuestion = { id: genId(), text: "", type: "radio", options: ["Option 1", "Option 2"], required: true };
      updateConfig({ questions: [...config.questions, newQ] });
    };

    const removeQuestion = (qId: string) =>
      updateConfig({ questions: config.questions.filter((q) => q.id !== qId) });

    const moveQuestion = (qIdx: number, dir: -1 | 1) => {
      const newIdx = qIdx + dir;
      if (newIdx < 0 || newIdx >= config.questions.length) return;
      const qs = [...config.questions];
      [qs[qIdx], qs[newIdx]] = [qs[newIdx], qs[qIdx]];
      updateConfig({ questions: qs });
    };

    const addOption = (qId: string) => {
      const q = config.questions.find((q) => q.id === qId);
      if (!q) return;
      updateQuestion(qId, { options: [...q.options, `Option ${q.options.length + 1}`] });
    };

    const updateOption = (qId: string, idx: number, value: string) => {
      const q = config.questions.find((q) => q.id === qId);
      if (!q) return;
      const opts = [...q.options];
      opts[idx] = value;
      updateQuestion(qId, { options: opts });
    };

    const removeOption = (qId: string, idx: number) => {
      const q = config.questions.find((q) => q.id === qId);
      if (!q || q.options.length <= 1) return;
      const opts = q.options.filter((_, i) => i !== idx);
      updateQuestion(qId, { options: opts });
    };

    const QUESTION_TYPE_CONFIG: Record<string, { icon: React.ReactNode; label: string }> = {
      radio: { icon: <RadioButtonChecked sx={{ fontSize: 16 }} />, label: "Single Choice (Radio)" },
      checkbox: { icon: <CheckBoxIcon sx={{ fontSize: 16 }} />, label: "Multiple Choice (Checkbox)" },
      text: { icon: <Notes sx={{ fontSize: 16 }} />, label: "Text Answer" },
      dropdown: { icon: <ArrowDropDown sx={{ fontSize: 16 }} />, label: "Dropdown" },
    };

    return (
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 860, mx: "auto" }}>
        <Breadcrumbs separator={<NavigateNext sx={{ fontSize: 16 }} />} sx={{ mb: 3 }}>
          <Link underline="hover" sx={{ cursor: "pointer", color: COLORS.textMuted, fontSize: "0.82rem" }} onClick={() => setActiveItemId(null)}>
            {phase.title}
          </Link>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Quiz sx={{ fontSize: 16, color: COLORS_SURVEY }} />
            <Typography sx={{ fontSize: "0.82rem", color: COLORS.text, fontWeight: 600 }}>Survey / Quiz</Typography>
          </Box>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: "10px", bgcolor: COLORS_SURVEY_BG, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Quiz sx={{ color: COLORS_SURVEY, fontSize: 20 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={700} color={COLORS.text}>Survey / Quiz Builder</Typography>
            <Typography variant="caption" color="text.secondary">
              {config.questions.length} question{config.questions.length !== 1 ? "s" : ""} Â· {isQuiz ? "Quiz mode (graded)" : "Survey mode"}
            </Typography>
          </Box>
          <Tooltip title="Duplicate" arrow>
            <IconButton size="small" onClick={() => duplicateItem(phase.id, item.id)} sx={{ color: COLORS.textMuted }}>
              <ContentCopy sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Survey Settings */}
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: "12px", border: `1px solid ${COLORS.border}`, mb: 2.5 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2, color: COLORS.text }}>Settings</Typography>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid size={{ xs: 12, sm: 5 }}>
              <TextField
                fullWidth label="Survey Title (English)" value={item.title}
                onChange={(e) => updateItem(phase.id, item.id, { title: e.target.value })}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 5 }}>
              <TextField
                fullWidth label="Survey Title (العربية)" value={item.titleAr || ""}
                onChange={(e) => updateItem(phase.id, item.id, { titleAr: e.target.value })}
                helperText="Optional — falls back to English when empty"
                sx={ARABIC_INPUT_SX}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Mode</InputLabel>
                <Select
                  value={config.mode} label="Mode"
                  onChange={(e) => updateConfig({ mode: e.target.value as "survey" | "quiz" })}
                  sx={{ borderRadius: "10px" }}
                >
                  <MenuItem value="survey">Survey</MenuItem>
                  <MenuItem value="quiz">Quiz (graded)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {isQuiz && (
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth type="number" label="Passing Score (%)"
                  value={config.passingScore ?? 60}
                  onChange={(e) => updateConfig({ passingScore: Math.min(100, Math.max(0, Number(e.target.value))) })}
                  InputProps={{ inputProps: { min: 0, max: 100 } }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                />
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              <BilingualField
                label="Description (optional)"
                valueEn={config.description ?? ""}
                valueAr={config.descriptionAr ?? ""}
                onChangeEn={(v) => updateConfig({ description: v })}
                onChangeAr={(v) => updateConfig({ descriptionAr: v })}
                placeholderEn="Instructions shown to students before they start..."
                placeholderAr="تعليمات تُعرض على الطلاب قبل البدء..."
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Questions */}
        {config.questions.length === 0 ? (
          <Paper
            elevation={0}
            sx={{ p: 5, borderRadius: "14px", border: `2px dashed ${COLORS.border}`, textAlign: "center", cursor: "pointer", transition: "all 0.2s", "&:hover": { borderColor: COLORS_SURVEY, bgcolor: COLORS_SURVEY_BG } }}
            onClick={addQuestion}
          >
            <Quiz sx={{ fontSize: 40, color: COLORS_SURVEY, mb: 1.5 }} />
            <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5, color: COLORS.text }}>No questions yet</Typography>
            <Typography variant="body2" color="text.secondary">Click to add your first question</Typography>
          </Paper>
        ) : (
          <Stack spacing={2}>
            {config.questions.map((q, qIdx) => (
              <Paper key={q.id} elevation={0} sx={{ p: 2.5, borderRadius: "12px", border: `1px solid ${COLORS.border}`, "&:hover": { borderColor: `${COLORS_SURVEY}50` } }}>
                {/* Question header row */}
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2 }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: COLORS_SURVEY_BG, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, mt: 0.3 }}>
                    <Typography variant="caption" fontWeight={800} sx={{ color: COLORS_SURVEY, fontSize: "0.75rem" }}>Q{qIdx + 1}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <TextField
                      fullWidth size="small"
                      placeholder={`Question ${qIdx + 1} text...`}
                      value={q.text}
                      onChange={(e) => updateQuestion(q.id, { text: e.target.value })}
                      sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: "8px", fontWeight: 600 } }}
                    />
                    {/* Type + Required row */}
                    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Answer Type</InputLabel>
                        <Select
                          value={q.type} label="Answer Type"
                          onChange={(e) => {
                            const newType = e.target.value as SurveyQuestion["type"];
                            const needsOptions = ["radio", "checkbox", "dropdown"].includes(newType);
                            updateQuestion(q.id, {
                              type: newType,
                              options: needsOptions && q.options.length === 0 ? ["Option 1", "Option 2"] : q.options,
                              correctAnswer: undefined,
                            });
                          }}
                          sx={{ borderRadius: "8px" }}
                        >
                          {Object.entries(QUESTION_TYPE_CONFIG).map(([t, cfg]) => (
                            <MenuItem key={t} value={t}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                {cfg.icon} {cfg.label}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControlLabel
                        control={<Switch checked={q.required} onChange={(_, v) => updateQuestion(q.id, { required: v })} size="small" sx={{ "& .MuiSwitch-track": { bgcolor: q.required ? `${COLORS_SURVEY} !important` : undefined } }} />}
                        label={<Typography variant="caption" fontWeight={600}>Required</Typography>}
                      />
                    </Box>
                  </Box>
                  {/* Move + Delete */}
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
                    <IconButton size="small" onClick={() => moveQuestion(qIdx, -1)} disabled={qIdx === 0} sx={{ p: 0.4, opacity: 0.5, "&:hover": { opacity: 1 } }}>
                      <ArrowUpward sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton size="small" onClick={() => moveQuestion(qIdx, 1)} disabled={qIdx === config.questions.length - 1} sx={{ p: 0.4, opacity: 0.5, "&:hover": { opacity: 1 } }}>
                      <ArrowDownward sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton size="small" onClick={() => removeQuestion(q.id)} sx={{ p: 0.4, color: COLORS.error, opacity: 0.6, "&:hover": { opacity: 1 } }}>
                      <Delete sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </Box>

                {/* Options (for radio / checkbox / dropdown) */}
                {["radio", "checkbox", "dropdown"].includes(q.type) && (
                  <Box sx={{ pl: 5 }}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 1, display: "block" }}>
                      Options {isQuiz ? "Â· Mark correct answer below" : ""}
                    </Typography>
                    <Stack spacing={1} sx={{ mb: 1 }}>
                      {q.options.map((opt, oIdx) => {
                        const isCorrectOpt = q.type === "checkbox"
                          ? Array.isArray(q.correctAnswer) && (q.correctAnswer as string[]).includes(opt)
                          : q.correctAnswer === opt;
                        return (
                          <Box
                            key={oIdx}
                            sx={{
                              display: "flex", alignItems: "center", gap: 1,
                              borderRadius: "8px",
                              ...(isQuiz && {
                                border: `1.5px solid ${isCorrectOpt ? "#10B981" : "rgba(0,0,0,0.07)"}`,
                                bgcolor: isCorrectOpt ? "#F0FDF480" : "transparent",
                                p: 0.6,
                                transition: "all 0.15s",
                              }),
                            }}
                          >
                            {isQuiz && (
                              <Tooltip
                                title={isCorrectOpt
                                  ? (q.type === "checkbox" ? "Remove from correct answers" : "Unset correct answer")
                                  : (q.type === "checkbox" ? "Add as correct answer" : "Set as correct answer")}
                                arrow
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    if (q.type === "checkbox") {
                                      const ca = Array.isArray(q.correctAnswer) ? [...(q.correctAnswer as string[])] : [];
                                      const i = ca.indexOf(opt);
                                      if (i >= 0) ca.splice(i, 1); else ca.push(opt);
                                      updateQuestion(q.id, { correctAnswer: ca });
                                    } else {
                                      updateQuestion(q.id, { correctAnswer: q.correctAnswer === opt ? undefined : opt });
                                    }
                                  }}
                                  sx={{ p: 0.4, color: isCorrectOpt ? "#10B981" : "#D1D5DB", flexShrink: 0 }}
                                >
                                  <CheckCircle sx={{ fontSize: 20 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            <TextField
                              size="small" fullWidth value={opt}
                              onChange={(e) => updateOption(q.id, oIdx, e.target.value)}
                              placeholder={`Option ${oIdx + 1}`}
                              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Typography variant="caption" sx={{ color: "#9CA3AF", fontWeight: 600, minWidth: 16 }}>{oIdx + 1}.</Typography>
                                  </InputAdornment>
                                ),
                                endAdornment: isQuiz && isCorrectOpt ? (
                                  <InputAdornment position="end">
                                    <Typography variant="caption" sx={{ color: "#10B981", fontWeight: 700, fontSize: "0.72rem", whiteSpace: "nowrap" }}>
                                      âœ" Correct
                                    </Typography>
                                  </InputAdornment>
                                ) : undefined,
                              }}
                            />
                            <IconButton size="small" onClick={() => removeOption(q.id, oIdx)} disabled={q.options.length <= 1} sx={{ p: 0.3, color: "#9CA3AF", "&:hover": { color: COLORS.error }, flexShrink: 0 }}>
                              <Close sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                        );
                      })}
                    </Stack>
                    {/* Warning: no correct answer set */}
                    {isQuiz && (!q.correctAnswer || (Array.isArray(q.correctAnswer) && q.correctAnswer.length === 0)) && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                        <Warning sx={{ fontSize: 13, color: "#F59E0B" }} />
                        <Typography variant="caption" sx={{ color: "#F59E0B", fontWeight: 600, fontSize: "0.72rem" }}>
                          No correct answer set â€" click âœ" next to an option to mark the correct answer
                        </Typography>
                      </Box>
                    )}
                    <Button
                      size="small" startIcon={<Add />} onClick={() => addOption(q.id)}
                      sx={{ textTransform: "none", color: COLORS_SURVEY, fontSize: "0.75rem", borderRadius: "8px", py: 0.4 }}
                    >
                      Add Option
                    </Button>
                  </Box>
                )}

                {/* Text answer preview */}
                {q.type === "text" && (
                  <Box sx={{ pl: 5 }}>
                    {isQuiz && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.2 }}>
                        <Warning sx={{ fontSize: 14, color: "#F59E0B" }} />
                        <Typography variant="caption" sx={{ color: "#F59E0B", fontWeight: 600 }}>
                          Text answers are not auto-graded and will not count toward the quiz score
                        </Typography>
                      </Box>
                    )}
                    <TextField
                      size="small" fullWidth disabled value=""
                      placeholder="Student's text answer will appear here..."
                      multiline rows={2}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: "rgba(0,0,0,0.02)" } }}
                    />
                  </Box>
                )}
              </Paper>
            ))}
          </Stack>
        )}

        {/* Add Question Button */}
        {config.questions.length > 0 && (
          <Button
            variant="outlined" startIcon={<Add />} onClick={addQuestion} fullWidth
            sx={{ mt: 2, borderRadius: "10px", textTransform: "none", fontWeight: 600, borderColor: `${COLORS_SURVEY}60`, color: COLORS_SURVEY, "&:hover": { borderColor: COLORS_SURVEY, bgcolor: COLORS_SURVEY_BG }, py: 1.2 }}
          >
            Add Question
          </Button>
        )}
      </Box>
    );
  };

  // ===== CONTENT ROUTER =====
  const renderContentEditor = () => {
    const active = getActiveItem();
    if (!active) return renderCourseDetails();
    const { phase, item } = active;
    switch (item.type) {
      case "video": return renderVideoEditor(phase, item);
      case "pdf": return renderPdfEditor(phase, item);
      case "letter": return renderLetterEditor(phase, item);
      case "survey": return renderSurveyEditor(phase, item);
      default: return null;
    }
  };

  // ===== LOADING =====
  if (loading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 400, gap: 2 }}>
        <CircularProgress sx={{ color: COLORS.accent }} />
        <Typography variant="body2" color="text.secondary">Loading course...</Typography>
      </Box>
    );
  }

  // ===== MAIN RENDER =====
  return (
    <Box sx={{ height: "calc(100vh - 120px)", display: "flex", flexDirection: "column" }}>
      {/* Top Bar */}
      <Paper elevation={0} sx={{ px: { xs: 1.5, md: 2.5 }, py: 1.2, borderRadius: "14px", border: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <Tooltip title="Back to Courses" arrow>
          <IconButton onClick={() => navigate("/admin/courses")} size="small" sx={{ bgcolor: "rgba(0,0,0,0.03)", "&:hover": { bgcolor: "rgba(0,0,0,0.06)" } }}>
            <ArrowBack sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>

        {isMobile && (
          <IconButton onClick={() => setSidebarOpen(!sidebarOpen)} size="small">
            <MenuIcon />
          </IconButton>
        )}

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <School sx={{ color: COLORS.accent, fontSize: 22 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ color: COLORS.text, lineHeight: 1.2 }}>
            {title || "Untitled Course"}
          </Typography>
          <Typography variant="caption" sx={{ color: COLORS.textLight, fontSize: "0.68rem" }}>
            {stats.phaseCount} phase{stats.phaseCount !== 1 ? "s" : ""} &middot; {stats.totalItems} item{stats.totalItems !== 1 ? "s" : ""}
          </Typography>
        </Box>

        {hasChanges && (
          <Chip
            icon={<Warning sx={{ fontSize: "14px !important" }} />}
            label="Unsaved"
            size="small"
            sx={{ height: 26, fontSize: "0.7rem", bgcolor: "rgba(245,158,11,0.1)", color: COLORS.warning, fontWeight: 600, "& .MuiChip-icon": { color: COLORS.warning } }}
          />
        )}

        <Chip
          label={status}
          size="small"
          sx={{
            textTransform: "capitalize", fontWeight: 600, height: 26,
            bgcolor: status === "published" ? "#ECFDF5" : status === "archived" ? "rgba(21,101,192,0.08)" : "rgba(245,158,11,0.1)",
            color: status === "published" ? COLORS.success : status === "archived" ? COLORS.textMuted : COLORS.warning,
          }}
        />

        <Button
          variant="contained" startIcon={saving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <Save />}
          onClick={handleSave} disabled={saving}
          sx={{
            bgcolor: COLORS.accent, "&:hover": { bgcolor: COLORS.accentDark },
            borderRadius: "10px", textTransform: "none", fontWeight: 600, px: isMobile ? 2 : 3,
            boxShadow: "0 4px 14px rgba(21,101,192,0.35)",
          }}
        >
          {saving ? "Saving..." : "Save Course"}
        </Button>
      </Paper>

      {saving && <LinearProgress sx={{ mb: 1, borderRadius: 1, "& .MuiLinearProgress-bar": { bgcolor: COLORS.accent } }} />}

      {error && (
        <Fade in><Alert severity="error" onClose={() => setError("")} sx={{ mb: 1.5, borderRadius: "10px" }}>{error}</Alert></Fade>
      )}

      {/* Main Content */}
      <Paper elevation={0} sx={{ flex: 1, borderRadius: "14px", border: `1px solid ${COLORS.border}`, overflow: "hidden", display: "flex", minHeight: 0 }}>
        {isMobile ? (
          <Drawer open={sidebarOpen} onClose={() => setSidebarOpen(false)} PaperProps={{ sx: { width: PHASE_SIDEBAR_WIDTH, borderRadius: "0 14px 14px 0" } }}>
            {renderSidebar()}
          </Drawer>
        ) : (
          renderSidebar()
        )}
        <Box sx={{ flex: 1, overflow: "auto", bgcolor: COLORS.white }}>
          {renderContentEditor()}
        </Box>
      </Paper>

      {/* Add Item Dialog */}
      <Dialog open={addItemDialogOpen} onClose={() => setAddItemDialogOpen(false)} PaperProps={{ sx: { borderRadius: "16px", minWidth: 380 } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 0.5, pt: 3 }}>Add Content</DialogTitle>
        <Typography variant="body2" color="text.secondary" sx={{ px: 3, mb: 2 }}>
          What type of content would you like to add?
        </Typography>
        <DialogContent sx={{ pt: 0 }}>
          <Stack spacing={1.5}>
            {([
              { type: "video" as const, label: "Video", icon: <VideoLibrary />, color: COLORS.video, bg: COLORS.videoBg, desc: "Select from your video library" },
              { type: "pdf" as const, label: "PDF Document", icon: <PictureAsPdf />, color: COLORS.pdf, bg: COLORS.pdfBg, desc: "Upload a PDF file for students" },
              { type: "letter" as const, label: "Written Content", icon: <TextFields />, color: COLORS.letter, bg: COLORS.letterBg, desc: "Write text, instructions, or a letter" },
              { type: "survey" as const, label: "Survey / Quiz", icon: <Quiz />, color: COLORS_SURVEY, bg: COLORS_SURVEY_BG, desc: "Build a survey or graded quiz for students" },
            ]).map((opt) => (
              <Paper
                key={opt.type} elevation={0}
                onClick={() => setAddItemType(opt.type)}
                sx={{
                  p: 2, display: "flex", alignItems: "center", gap: 2, cursor: "pointer",
                  borderRadius: "12px",
                  border: addItemType === opt.type ? `2px solid ${opt.color}` : `2px solid ${COLORS.border}`,
                  bgcolor: addItemType === opt.type ? `${opt.color}08` : "transparent",
                  transition: "all 0.15s",
                  "&:hover": { borderColor: opt.color },
                }}
              >
                <Box sx={{ width: 44, height: 44, borderRadius: "12px", bgcolor: opt.bg, display: "flex", alignItems: "center", justifyContent: "center", color: opt.color, flexShrink: 0 }}>
                  {opt.icon}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={600}>{opt.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{opt.desc}</Typography>
                </Box>
                {addItemType === opt.type && (
                  <CheckCircle sx={{ color: opt.color, fontSize: 22 }} />
                )}
              </Paper>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setAddItemDialogOpen(false)} sx={{ borderRadius: "10px", textTransform: "none", color: COLORS.textMuted }}>Cancel</Button>
          <Button onClick={confirmAddItem} variant="contained" sx={{ bgcolor: COLORS.accent, "&:hover": { bgcolor: COLORS.accentDark }, borderRadius: "10px", textTransform: "none", fontWeight: 600, px: 3 }}>
            Add Item
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} PaperProps={{ sx: { borderRadius: "14px", maxWidth: 380 } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          {deleteConfirm?.type === "phase" ? "Delete Phase" : "Delete Item"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {deleteConfirm?.type === "phase"
              ? "This will delete the phase and all its items. This action cannot be undone."
              : "Are you sure you want to remove this item?"}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteConfirm(null)} sx={{ borderRadius: "8px", textTransform: "none" }}>Cancel</Button>
          <Button
            onClick={() => {
              if (!deleteConfirm) return;
              if (deleteConfirm.type === "phase") removePhase(deleteConfirm.phaseId);
              else if (deleteConfirm.itemId) removeItem(deleteConfirm.phaseId, deleteConfirm.itemId);
            }}
            variant="contained"
            sx={{ bgcolor: COLORS.error, "&:hover": { bgcolor: "#DC2626" }, borderRadius: "8px", textTransform: "none", fontWeight: 600 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Video Preview Dialog */}
      <Dialog
        open={!!videoPreview}
        onClose={() => setVideoPreview(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "14px", overflow: "hidden" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap>
            {videoPreview?.title || "Video Preview"}
          </Typography>
          <IconButton onClick={() => setVideoPreview(null)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {videoPreviewPlayback.kind !== "none" ? (
            <Box sx={{ width: "100%", aspectRatio: "16/9", bgcolor: "#000" }}>
              <VideoPlayer
                playback={videoPreviewPlayback}
                title={videoPreview?.title || "Video Preview"}
                poster={videoPreview?.thumbnailUrl}
              />
            </Box>
          ) : (
            <Alert severity="warning" sx={{ m: 2 }}>
              This video has no playable source yet. If it was just uploaded, give Cloudflare a
              moment to finish encoding — otherwise re-select it from the video gallery and save.
            </Alert>
          )}
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ borderRadius: "10px", fontWeight: 500 }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CourseEditor;


